<template>
  <div class="w-full">
    <div class="flex items-center justify-between">
      <h3 class="py-0 my-0 text-xl text-gray-800">
        Aktionsblog
      </h3>

      <mia-submit-button
        type="button"
        label="Neuer Eintrag"
        @click="$router.push({ name: 'b2b.campaignBlog.create' })"
      />
    </div>

    <mia-wait for="loading filters">
      <template #waiting>
        <div class="flex items-center justify-center py-8">
          <mia-wave-loader />
        </div>
      </template>

      <template #default>
        <div class="grid grid-cols-5 gap-6 p-4 mt-6 -mx-2 bg-gray-200">
          <mia-feature-select
            v-model="filters.preset"
            label="Vorlage"
            :options="presets"
            name-by="label"
            track-by="id"
            disable-default-option
          />
          <mia-feature-input
            v-model="filters.publishedFrom"
            label="Veröffentlicht von"
            type="datetime-local"
            @blur="loadBlogEntries"
          />
          <mia-feature-input
            v-model="filters.publishedTo"
            label="Veröffentlicht bis"
            type="datetime-local"
            @blur="loadBlogEntries"
          />
          <mia-feature-select
            v-model="filters.author"
            label="Mitarbeiter"
            :options="authors"
            :name-by="(author) => `${author.item_firstname} ${author.item_lastname}`"
            track-by="id"
          />

          <div class="flex items-end justify-end">
            <mia-cancel-button
              label="Zurücksetzen"
              @click="resetFilters"
            />
          </div>
        </div>

        <div class="mt-6">
          <mia-wait for="loading blog entries">
            <template #waiting>
              <div class="flex items-center justify-center py-8">
                <mia-wave-loader />
              </div>
            </template>

            <template #default>
              <datatable
                :columns="columns"
                :data="entries"
                class="sticky w-full table-auto datatable"
              >
                <template #no-results>
                  <div class="text-center">
                    Für die gewählten Filter wurden keine Einträge gefunden.
                  </div>
                </template>
              </datatable>
            </template>
          </mia-wait>
        </div>
      </template>
    </mia-wait>

    <mia-confirmation-modal
      v-if="deletingBlogEntry"
      id="delete-blog-entry-confirmation-modal"
      :show="deletingBlogEntry !== null"
      title="Eintrag löschen"
      confirm-classes="border-red-500 bg-red-500 text-white hover:bg-red-600 hover:border-red-600"
      confirm-text="Eintrag löschen"
      cancel-text="Abbrechen"
      width="40%"
      :is-loading="$wait.is('deleting blog entry')"
      @closed="deletingBlogEntry = null"
      @cancel="deletingBlogEntry = null"
      @confirm="onDeleteBlogEntryConfirmed"
    >
      <div class="p-4">
        Sind Sie sicher, dass Sie den Eintrag <b>{{ deletingBlogEntry.item_title }}</b> endgültig löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
      </div>
    </mia-confirmation-modal>
  </div>
</template>

<script>
import FeatureSelect from '@/components/cars/edit/FeatureSelect.vue'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import { DateTime } from 'luxon'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import { deleteBlogEntry, getBlogEntries, getBlogFilters } from '@/api/b2b'
import ActionButtonColumn from '@/components/ui/datatable/columns/ActionButtonColumn.vue'
import LinkColumn from '@/components/ui/datatable/columns/LinkColumn.vue'
import CancelButton from '@/components/cars/edit/CancelButton.vue'
import LabelColumn from '@/components/ui/datatable/columns/LabelColumn.vue'

const PRESETS = {
  published: {
    id: 'published',
    label: 'Veröffentlichte Einträge'
  },
  unpublished: {
    id: 'unpublished',
    label: 'Nicht länger veröffentlichte Einträge'
  },
  notYetPublished: {
    id: 'not_yet_published',
    label: 'Anstehende Einträge'
  },
  all: {
    id: 'all',
    label: 'Alle anzeigen'
  }
}

export default {
  components: {
    'mia-cancel-button': CancelButton,
    'mia-feature-input': FeatureInput,
    'mia-feature-select': FeatureSelect,
    'mia-submit-button': SubmitButton
  },

  data() {
    return {
      presets: Object.values(PRESETS),
      authors: [],
      entries: [],
      filters: {
        preset: PRESETS.all.id,
        publishedFrom: '',
        publishedTo: '',
        author: ''
      },
      columns: [
        {
          field: 'item_title',
          label: 'Titel',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/b2b/campaign-blog/:id'
          }
        },
        {
          field: 'item_published_at',
          label: 'Veröffentlicht ab',
          headerAlign: 'left',
          representedAs: row => this.formatDateTime(row.item_published_at)
        },
        {
          field: 'item_unpublished_at',
          label: 'Veröffentlicht bis',
          headerAlign: 'left',
          representedAs: row => (row.item_unpublished_at ? this.formatDateTime(row.item_unpublished_at) : '-')
        },
        {
          field: 'item_attachments',
          label: 'Anhänge',
          headerAlign: 'left',
          representedAs: row => (row.item_attachments.length > 0 ? row.item_attachments.length : '-')
        },
        {
          field: 'item_firstname',
          label: 'Autor',
          headerAlign: 'left',
          representedAs: row => `${row.item_user_firstname} ${row.item_user_lastname}`
        },
        {
          field: '',
          label: 'Status',
          headerAlign: 'left',
          component: LabelColumn,
          meta: {
            labelProp: row => this.getStatusLabel(row),
            labelClasses: row => this.getStatusClasses(row)
          }
        },
        {
          field: '',
          label: 'Aktion',
          headerAlign: 'left',
          component: ActionButtonColumn,
          meta: {
            actions: [
              {
                icon: 'pencil-write',
                onClick: row => this.onEdit(row)
              },
              {
                icon: 'bin-1',
                class: 'border-red-500 text-red-500 hover:bg-red-500',
                onClick: row => this.onDelete(row)
              }
            ]
          }
        }
      ],
      deletingBlogEntry: null
    }
  },

  watch: {
    'filters.preset': {
      immediate: true,
      handler(preset) {
        this.applyPreset(preset)
      }
    },

    'filters.author': function () {
      this.loadBlogEntries()
    }
  },

  created() {
    this.loadFilters()
  },

  mounted() {
    this.$nextTick(() => {
      eventBus.$emit('mia-set-breadcrumb', [
        {
          link: {
            path: '/b2b'
          },
          text: 'B2B'
        },
        {
          link: {
            path: '/b2b/campaign-blog'
          },
          text: 'Aktionsblog'
        }
      ])
    })
  },

  methods: {
    resetFilters() {
      this.$set(this.filters, 'preset', PRESETS.all.id)
      this.$set(this.filters, 'publishedFrom', '')
      this.$set(this.filters, 'publishedTo', '')
      this.$set(this.filters, 'author', '')
    },

    applyPreset() {
      this.$set(this.filters, 'publishedFrom', '')
      this.$set(this.filters, 'publishedTo', '')

      this.$nextTick(() => {
        this.loadBlogEntries()
      })
    },

    async loadFilters() {
      this.$wait.start('loading filters')
      const [error, response] = await getBlogFilters()
      this.$wait.end('loading filters')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Filter konnten nicht geladen werden']
        })
        return
      }

      this.authors = response.authors
    },

    async loadBlogEntries() {
      this.$wait.start('loading blog entries')
      const [error, response] = await getBlogEntries({
        preset: this.filters.preset,
        published_from: this.dateTimeLocalToDateTime(this.filters.publishedFrom || ''),
        published_to: this.dateTimeLocalToDateTime(this.filters.publishedTo || ''),
        author: this.filters.author || ''
      })
      this.$wait.end('loading blog entries')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Blogeinträge konnten nicht geladen werden']
        })
        return
      }

      this.entries = response
    },

    formatDateTime(dateStr) {
      return `${DateTime.fromSQL(dateStr).toFormat('dd.MM.yyyy, HH:mm')} Uhr`
    },

    onEdit(blogEntry) {
      this.$router.push(`/b2b/campaign-blog/${blogEntry.id}`)
    },

    onDelete(blogEntry) {
      this.deletingBlogEntry = blogEntry
    },

    async onDeleteBlogEntryConfirmed() {
      this.$wait.start('deleting blog entry')
      const [error] = await deleteBlogEntry(this.deletingBlogEntry.id)
      this.$wait.end('deleting blog entry')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Der Eintrag konnte nicht gelöscht werden']
        })
        return
      }

      window.eventBus.$emit('mia-info', {
        info: ['Der Eintrag wurde erfolgreich gelöscht']
      })
      this.deletingBlogEntry = null
      this.loadBlogEntries()
    },

    dateTimeLocalToDateTime(dateStr) {
      if (!dateStr) {
        return ''
      }

      return DateTime.fromFormat(dateStr, 'yyyy-MM-dd\'T\'HH:mm').toFormat('yyyy-MM-dd HH:mm:ss')
    },

    getStatus(blogEntry) {
      const publishedAt = DateTime.fromSQL(blogEntry.item_published_at)

      if (!blogEntry.item_unpublished_at) {
        if (DateTime.local() >= publishedAt) {
          return 'published'
        }

        if (DateTime.local() < publishedAt) {
          return 'notYetPublished'
        }
      }

      if (blogEntry.item_unpublished_at) {
        const unpublishedAt = DateTime.fromSQL(blogEntry.item_unpublished_at)

        if (DateTime.local() >= publishedAt && DateTime.local() < unpublishedAt) {
          return 'published'
        }

        if (DateTime.local() >= publishedAt && DateTime.local() >= unpublishedAt) {
          return 'unpublished'
        }
      }

      return ''
    },

    getStatusLabel(blogEntry) {
      return {
        published: 'Veröffentlicht',
        unpublished: 'Abgelaufen',
        notYetPublished: 'Anstehend'
      }[this.getStatus(blogEntry)] || ''
    },

    getStatusClasses(blogEntry) {
      return {
        published: 'px-3 py-1 rounded-full text-center inline-block bg-green-200 border border-green-300 text-sm',
        unpublished: 'px-3 py-1 rounded-full text-center inline-block bg-red-200 border border-red-300 text-sm',
        notYetPublished: 'px-3 py-1 rounded-full text-center inline-block bg-gray-300 border border-gray-400 text-sm'
      }[this.getStatus(blogEntry)] || ''
    }
  }
}
</script>
